import { useRefreshTick } from "../lib/useAutoRefresh";
import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { api, apiErr } from "../lib/api";
import { money, FREQ_LABELS } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { useClientPerms } from "../lib/useClientPerms";
import PayCalendarPanel from "../components/PayCalendarPanel";
import TimeSettingsPanel from "../components/TimeSettingsPanel";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Plus, Pencil, Trash2, ArrowLeft, PlayCircle, FileText, Users, FileWarning, Scale, History, Landmark, BarChart3, Lock, Eye, Clock, UserCog } from "lucide-react";
import ClientLogins from "../components/ClientLogins";
import { DirectDepositSetup } from "../components/DirectDepositPanel";
import CompanyFormDialog from "../components/CompanyFormDialog";
import OnboardingPanel from "../components/OnboardingPanel";
import { DemoWalkthrough } from "../components/DemoWalkthrough";
import { DeleteCompanyDialog } from "../components/DeleteCompanyDialog";
import MigrationReadinessBanner from "../components/MigrationReadinessBanner";
import NextRemittance from "../components/NextRemittance";
import { CompanyReadinessSummary } from "../components/ReadinessPanel";
import { pdfUrl } from "../lib/api";

const EMPTY = (company_id, freq) => ({
  company_id, first_name: "", last_name: "", preferred_name: "", sin: "", email: "", phone: "",
  address_line1: "", apartment_unit: "", city: "", province: "ON", postal_code: "",
  date_of_birth: "", hire_date: "", termination_date: "",
  pay_type: "salary", annual_salary: 60000, hourly_rate: 25, standard_hours: 80,
  pay_frequency: freq || "biweekly", td1_federal_claim: "", td1_provincial_claim: "",
  rpp_contribution: 0, vacation_rate: 4, vacation_mode: "accrue",
  custom_earnings: [], custom_deductions: [],
  bank_name: "", bank_institution: "", bank_transit: "", bank_account: "",
  job_title: "", employment_type: "", pay_method: "Direct Deposit", standard_hours_per_week: "",
  emergency_contact_name: "", emergency_contact_relationship: "", emergency_contact_phone: "",
  ei_insurable_type: "", wsib_code: "", vacation_accrual_method: "", vacation_pay_start_date: "",
  additional_tax_per_pay: 0, internal_notes: "",
  cpp_applicable: true, ei_applicable: true, wsib_applicable: false,
  health_dental: false, rrsp_pension: false, union_dues: false,
  td1_federal_completed: false, td1_provincial_completed: false, basic_personal_only: false,
  status: "active",
});

function ageFrom(dob) {
  if (!dob) return null;
  const b = new Date(dob), n = new Date();
  let a = n.getFullYear() - b.getFullYear();
  const m = n.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && n.getDate() < b.getDate())) a--;
  return a;
}

export default function CompanyDetail() {
  const { companyId: paramId } = useParams();
  const { user } = useAuth();
  const companyId = paramId || user?.company_id;
  const navigate = useNavigate();
  const isClient = useLocation().pathname.startsWith("/client");
  const base = isClient ? "/client" : "";
  const { canView: canViewArea, canEdit: canEditArea } = useClientPerms();
  const opsLink = (area, path) => isClient ? `${base}${path}` : `${path}?company=${companyId}`;
  const [company, setCompany] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY(companyId));
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [roeEmp, setRoeEmp] = useState(null);
  const [viewEmp, setViewEmp] = useState(null);
  const [roe, setRoe] = useState({ reason: "A", first_day: "", last_day: "" });
  const [termData, setTermData] = useState(null);
  const [runs, setRuns] = useState([]);
  const [editOpen, setEditOpen] = useState(false);

  const currentYear = new Date().getFullYear();
  const ytdGross = runs
    .filter((r) => String(r.pay_date || "").startsWith(String(currentYear)))
    .reduce((s, r) => s + (r.totals?.gross || 0), 0);
  const lastRun = runs.reduce((a, r) => (!a || (r.pay_date || "") > (a.pay_date || "") ? r : a), null);

  const isStaff = user?.role === "super_admin";
  const canEditCompany = canEditArea("company");
  const canDeleteCompany = isStaff && (user?.staff_role || "master") === "master";

  const [deleteOpen, setDeleteOpen] = useState(false);
  const deleteCompany = () => setDeleteOpen(true);

  const nextRunDate = (() => {
    if (!lastRun?.pay_date) return null;
    const freq = company?.default_pay_frequency || "biweekly";
    const d = new Date(lastRun.pay_date + "T00:00:00");
    if (freq === "weekly") d.setDate(d.getDate() + 7);
    else if (freq === "monthly") d.setMonth(d.getMonth() + 1);
    else if (freq === "semimonthly") d.setDate(d.getDate() + 15);
    else d.setDate(d.getDate() + 14);
    return d.toISOString().slice(0, 10);
  })();
  const nextOverdue = nextRunDate && nextRunDate <= new Date().toISOString().slice(0, 10);

  const openTermination = async (e) => {
    try {
      const { data } = await api.get(`/termination/${companyId}/${e.employee_id}`);
      setTermData({ ...data, employee_id: e.employee_id });
    } catch (err) { toast.error(apiErr(err)); }
  };

  const load = useCallback(() => {
    api.get(`/companies/${companyId}`).then((r) => setCompany(r.data)).catch(() => {});
    api.get(`/employees?company_id=${companyId}`).then((r) => setEmployees(r.data)).catch(() => {});
    api.get(`/payroll/runs?company_id=${companyId}`).then((r) => setRuns(r.data)).catch(() => {});
  }, [companyId]);

  const tick = useRefreshTick();
  useEffect(() => { load(); }, [load, tick]);

  const openNew = () => {
    setEditing(null);
    const base = EMPTY(companyId, company?.default_pay_frequency);
    if (company) {
      base.vacation_rate = company.default_vacation_rate ?? base.vacation_rate;
      base.vacation_mode = company.default_vacation_mode ?? base.vacation_mode;
      base.custom_earnings = (company.default_earnings || []).map((e) => ({ ...e }));
      base.custom_deductions = (company.default_deductions || []).map((d) => ({ ...d }));
    }
    setForm(base);
    setOpen(true);
  };
  const openEdit = (emp) => {
    setEditing(emp.employee_id);
    setForm({ ...EMPTY(companyId, company?.default_pay_frequency), ...emp, td1_federal_claim: emp.td1_federal_claim ?? "", td1_provincial_claim: emp.td1_provincial_claim ?? "" });
    setOpen(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        annual_salary: Number(form.annual_salary) || 0,
        hourly_rate: Number(form.hourly_rate) || 0,
        standard_hours: Number(form.standard_hours) || 0,
        rpp_contribution: Number(form.rpp_contribution) || 0,
        td1_federal_claim: form.td1_federal_claim === "" ? null : Number(form.td1_federal_claim),
        td1_provincial_claim: form.td1_provincial_claim === "" ? null : Number(form.td1_provincial_claim),
      };
      if (editing) await api.put(`/employees/${editing}`, payload);
      else await api.post("/employees", payload);
      toast.success(editing ? "Employee updated" : "Employee added");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this employee?")) return;
    await api.delete(`/employees/${id}`);
    toast.success("Employee removed");
    load();
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setBool = (k) => (e) => setForm({ ...form, [k]: e.target.checked });

  const addLine = (key, blank) => setForm((f) => ({ ...f, [key]: [...(f[key] || []), blank] }));
  const setLine = (key, i, field, val) => setForm((f) => {
    const arr = [...(f[key] || [])];
    arr[i] = { ...arr[i], [field]: field === "amount" ? val : (field === "taxable" || field === "pre_tax" ? val : val) };
    return { ...f, [key]: arr };
  });
  const removeLine = (key, i) => setForm((f) => ({ ...f, [key]: (f[key] || []).filter((_, idx) => idx !== i) }));

  return (
    <div className="w-full">
      <CompanyFormDialog open={editOpen} onOpenChange={setEditOpen} company={company} onSaved={(c) => { setCompany(c); load(); }} />
      <Link to="/companies" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-black mb-4">
        <ArrowLeft className="h-3.5 w-3.5" /> All companies
      </Link>

      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="relative group">
          {company?.logo ? (
            <img src={company.logo} alt={company?.name} data-testid="company-logo-avatar" className="h-14 w-14 object-contain border border-border bg-white p-1" />
          ) : (
            <div className="h-14 w-14 bg-black text-white flex items-center justify-center font-black text-xl" data-testid="company-avatar">
              {(company?.name || "?")[0]?.toUpperCase()}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-4xl font-black tracking-tight">{company?.name || "…"}</h1>
          <p className="text-sm text-muted-foreground mt-1 font-mono">
            {company?.cra_business_number || "No BN"} · {company?.province} · {FREQ_LABELS[company?.default_pay_frequency]}
          </p>
        </div>
        {(isStaff || canEditCompany) && (
          <div className="flex items-center gap-2">
            {canEditCompany && (
              <button data-testid="hub-edit-company" onClick={() => setEditOpen(true)} className="inline-flex items-center gap-2 border border-border bg-white px-3 py-2 text-xs font-medium hover:border-black transition-colors" title="Edit company">
                <Pencil className="h-3.5 w-3.5" /> Edit
              </button>
            )}
            {canDeleteCompany && (
              <button data-testid="hub-delete-company" onClick={deleteCompany} className="inline-flex items-center gap-2 border border-border bg-white px-3 py-2 text-xs font-medium hover:border-red-600 hover:bg-red-600 hover:text-white transition-colors" title="Delete company">
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            )}
            {deleteOpen && <DeleteCompanyDialog company={company} postedRuns={runs.filter((r) => r.posted).length} onClose={() => setDeleteOpen(false)} onDeleted={() => navigate("/companies")} />}
          </div>
        )}
      </div>

      {company?.demo_tag === "postwage_demo" && <DemoWalkthrough companyId={companyId} />}

      {/* Company overview stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="border border-border bg-white p-4" data-testid="hub-stat-employees">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Active Employees</div>
          <div className="mt-2 text-2xl font-black tabular-nums">{employees.filter((e) => e.status === "active").length}</div>
        </div>
        <div className="border border-border bg-white p-4" data-testid="hub-stat-ytd-gross">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">YTD Gross ({currentYear})</div>
          <div className="mt-2 text-2xl font-black tabular-nums text-emerald-600">{money(ytdGross)}</div>
        </div>
        <div className="border border-border bg-white p-4" data-testid="hub-stat-last-run">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Last Payroll Run</div>
          <div className="mt-2 text-2xl font-black tabular-nums font-mono">{lastRun?.pay_date || "—"}</div>
        </div>
        <div className="border border-border bg-white p-4" data-testid="hub-stat-next-run">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
            Next Payroll Run
            {nextOverdue && <span className="text-[9px] text-amber-700 bg-amber-100 px-1 py-0.5">DUE</span>}
          </div>
          <div className={`mt-2 text-2xl font-black tabular-nums font-mono ${nextOverdue ? "text-amber-700" : ""}`}>{nextRunDate || "—"}</div>
        </div>
      </div>

      <PayCalendarPanel companyId={companyId} />

      {canViewArea("time") && <TimeSettingsPanel companyId={companyId} employees={employees} canEdit={canEditCompany} />}

      {/* Company-scoped workspace nav moved to the sticky CompanyWorkspaceNav header */}

      <MigrationReadinessBanner companyId={companyId} taxYear={currentYear} />
      {company?.migration_closed && isStaff && (
        <p className="mb-6 text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-testid="migration-closed-link">
          Migration closed {String(company.migration_closed_at || "").slice(0, 10)} · <Link to={`/migration?company=${companyId}`} className="underline hover:text-black">view / reopen</Link>
        </p>
      )}

      <CompanyReadinessSummary companyId={companyId} onEdit={canEditCompany ? () => setEditOpen(true) : null} />

      {canViewArea("remittance") && (
        <NextRemittance companyId={companyId} remittanceHref={opsLink("remittance", "/remittance")} variant="banner" />
      )}

      {isStaff && canViewArea("run_payroll") && company && <DirectDepositSetup company={company} onCompanyChange={load} />}

      <ClientLogins companyId={companyId} />
    </div>
  );
}


function F({ label, value, onChange, testid, type = "text", placeholder }) {
  return (
    <div>
      <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{label}</label>
      <input data-testid={testid} type={type} value={value} placeholder={placeholder} onChange={onChange} className="mt-1.5 w-full border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between py-1.5 border-b border-border last:border-0">
      <span className="text-muted-foreground">{k}</span>
      <span className="tabular-nums font-medium">{v}</span>
    </div>
  );
}

